// ── DB 관리 어셈블리 ──────────────────────────────────────────────────────────
// 테이블 목록 패널 + 스키마/데이터 상세 패널 + 행 편집 모달
import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { SchemaView }    from './SchemaView';
import { DataView }      from './DataView';
import { RowEditModal }  from './RowEditModal';
import { TABLE_DESCRIPTIONS } from './constants';
import { fmtNum } from './utils';
import { stdHeaderRow, stdPageTitle, stdPageSubtitle } from '../adminlayout/designStandard';
import {
  pageStyle, layoutStyle,
  tableListPanelStyle, mobileTableListPanelStyle,
  panelHeaderStyle, panelTitleStyle, badgeStyle, searchInputStyle,
  tableRowBtnStyle, tableRowBtnActiveStyle,
  tableNameStyle, tableDescStyle, tableMetaStyle,
  detailPanelStyle, mobileDetailPanelStyle, mobileBackBtnStyle,
  emptyStateStyle, detailHeaderStyle, detailTitleStyle, detailSubStyle,
  tableDescHeaderStyle, pageTagStyle, noteTagStyle,
  tabRowStyle, tabBtnStyle, placeholderStyle,
} from './styles';

export default function DbManagement() {
  // ── 테이블 목록 상태 ──────────────────────────────────────────────────────
  const [tables, setTables]               = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [search, setSearch]               = useState('');

  // ── 선택된 테이블 + 스키마 상태 ──────────────────────────────────────────
  const [selected, setSelected]           = useState(null);
  const [schema, setSchema]               = useState(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [activeTab, setActiveTab]         = useState('schema');

  // ── 데이터 탭 상태 ─────────────────────────────────────────────────────
  const [rows, setRows]           = useState([]);
  const [rowCols, setRowCols]     = useState([]);
  const [rowPkColumn, setRowPkColumn] = useState('id');
  const [pagination, setPagination]   = useState({ page: 1, limit: 50, total: 0, total_pages: 1 });
  const [rowsLoading, setRowsLoading] = useState(false);

  // ── 행 편집 모달 상태 ─────────────────────────────────────────────────
  const [editRow, setEditRow] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');

  // ── 모바일 반응형 ─────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ── 초기 테이블 목록 로드 ────────────────────────────────────────────
  useEffect(() => { loadTables(); }, []);

  async function loadTables() {
    setTablesLoading(true);
    const res = await apiClient.get('/api/admin/db/tables');
    setTablesLoading(false);
    if (res.ok) setTables(res.data?.data || []);
  }

  // ── 테이블 선택 → 스키마 로드 ────────────────────────────────────────
  async function selectTable(name) {
    setSelected(name);
    setActiveTab('schema');
    setRows([]);
    setRowCols([]);
    setRowPkColumn('id');
    setPagination({ page: 1, limit: 50, total: 0, total_pages: 1 });
    setEditRow(null);

    setSchemaLoading(true);
    const res = await apiClient.get(`/api/admin/db/tables/${encodeURIComponent(name)}`);
    setSchemaLoading(false);
    if (res.ok) setSchema(res.data?.data || null);
    else setSchema(null);
  }

  // ── 데이터 탭: 행 목록 로드 ──────────────────────────────────────────
  const loadRows = useCallback(async (tableName, page = 1, limit = 50) => {
    setRowsLoading(true);
    const res = await apiClient.get(
      `/api/admin/db/tables/${encodeURIComponent(tableName)}/rows?page=${page}&limit=${limit}`
    );
    setRowsLoading(false);
    if (res.ok) {
      setRows(res.data?.data?.rows || []);
      setRowCols(res.data?.data?.columns || []);
      // 서버가 pg_index로 감지한 실제 PK 컬럼명 사용
      setRowPkColumn(res.data?.data?.pk_column || 'id');
      setPagination(res.data?.data?.pagination || { page: 1, limit, total: 0, total_pages: 1 });
    }
  }, []);

  function switchToDataTab() {
    setActiveTab('data');
    if (selected) loadRows(selected, 1, 50);
  }

  // ── 행 저장 콜백 (RowEditModal에서 호출) ────────────────────────────
  async function handleRowSave(rowId, updates) {
    const res = await apiClient.patch(
      `/api/admin/db/tables/${encodeURIComponent(selected)}/rows/${encodeURIComponent(rowId)}`,
      updates
    );
    if (res.ok) {
      setSaveMsg('저장되었습니다.');
      setEditRow(null);
      loadRows(selected, pagination.page, pagination.limit);
      setTimeout(() => setSaveMsg(''), 2500);
    } else {
      return res.data?.message || '저장에 실패했습니다.';
    }
    return null;
  }

  async function openRowEdit(row) {
    const rowId = row?.[rowPkColumn];
    if (!selected || rowId === undefined || rowId === null) {
      setEditRow(row);
      return;
    }
    const res = await apiClient.get(
      `/api/admin/db/tables/${encodeURIComponent(selected)}/rows/${encodeURIComponent(rowId)}`
    );
    if (!res.ok) {
      window.alert(res.data?.message || '최신 행 데이터를 불러오지 못해 편집을 열지 않았습니다.');
      return;
    }
    const data = res.data?.data || {};
    if (Array.isArray(data.columns)) setRowCols(data.columns);
    if (data.pk_column) setRowPkColumn(data.pk_column);
    setEditRow(data.row || row);
  }

  // 검색: 영문 테이블명 + 한글 라벨/연결 화면명(TABLE_DESCRIPTIONS)까지 매칭
  const searchQ = search.trim().toLowerCase();
  const filteredTables = tables.filter((t) => {
    if (!searchQ) return true;
    if (t.table_name.toLowerCase().includes(searchQ)) return true;
    const desc = TABLE_DESCRIPTIONS[t.table_name];
    if (!desc) return false;
    return String(desc.label || '').toLowerCase().includes(searchQ)
      || (desc.pages || []).some((p) => String(p).toLowerCase().includes(searchQ));
  });

  // ── 테이블 목록 패널 (좌측) ──────────────────────────────────────────
  const TableListPanel = (
    <div style={isMobile ? mobileTableListPanelStyle : tableListPanelStyle}>
      <div style={panelHeaderStyle}>
        <span style={panelTitleStyle}>테이블 목록</span>
        <span style={badgeStyle}>{tables.length}</span>
      </div>
      <input style={searchInputStyle} placeholder="테이블명·한글 설명 검색..." value={search} onChange={(e) => setSearch(e.target.value)} />
      {tablesLoading
        ? <div style={placeholderStyle}>로딩 중...</div>
        : filteredTables.map((t) => {
          const desc = TABLE_DESCRIPTIONS[t.table_name];
          return (
            <button key={t.table_name} style={{ ...tableRowBtnStyle, ...(selected === t.table_name ? tableRowBtnActiveStyle : {}) }}
              onClick={() => selectTable(t.table_name)}>
              <span style={tableNameStyle}>{t.table_name}</span>
              {desc && <span style={tableDescStyle}>{desc.label}</span>}
              <div style={tableMetaStyle}>
                <span>{fmtNum(t.row_count)} 행</span><span>·</span>
                <span>{t.col_count} 컬럼</span><span>·</span>
                <span style={{ color: 'var(--text-muted)' }}>{t.total_size}</span>
              </div>
            </button>
          );
        })
      }
    </div>
  );

  // ── 상세 패널 (우측) ─────────────────────────────────────────────────
  const DetailPanelContent = (
    <div style={isMobile ? mobileDetailPanelStyle : detailPanelStyle}>
      {isMobile && selected && (
        <button style={mobileBackBtnStyle} onClick={() => { setSelected(null); setSchema(null); }}>← 테이블 목록</button>
      )}
      {!selected && (
        <div style={emptyStateStyle}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🗄️</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-sub)', marginBottom: 6 }}>테이블을 선택하세요</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {isMobile ? '위 목록에서 테이블을 탭하면' : '왼쪽 목록에서 테이블을 클릭하면'} 상세 정보를 확인할 수 있습니다.
          </div>
        </div>
      )}
      {selected && (
        <>
          <div style={detailHeaderStyle}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={detailTitleStyle}>{selected}</h3>
              {schema && <span style={detailSubStyle}>{fmtNum(schema.row_count)} 행 · {schema.columns?.length || 0} 컬럼 · PK: {rowPkColumn}</span>}
              {TABLE_DESCRIPTIONS[selected] && (() => {
                const d = TABLE_DESCRIPTIONS[selected];
                return (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                    <span style={tableDescHeaderStyle}>{d.label}</span>
                    {d.pages.map((p) => <span key={p} style={pageTagStyle}>{p}</span>)}
                    {d.note && <span style={noteTagStyle} title={d.note}>⚠ {d.note}</span>}
                  </div>
                );
              })()}
            </div>
            {saveMsg && <span style={{ fontSize: 12, color: '#16a34a', fontWeight: 700, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '4px 12px', whiteSpace: 'nowrap' }}>{saveMsg}</span>}
          </div>
          <div style={tabRowStyle}>
            <button style={tabBtnStyle(activeTab === 'schema')} onClick={() => setActiveTab('schema')}>스키마</button>
            <button style={tabBtnStyle(activeTab === 'data')}   onClick={switchToDataTab}>데이터 미리보기</button>
          </div>
          {activeTab === 'schema' && (
            schemaLoading ? <div style={placeholderStyle}>스키마 로딩 중...</div>
              : schema && <SchemaView schema={schema} isMobile={isMobile} />
          )}
          {activeTab === 'data' && (
            <DataView rows={rows} columns={rowCols} pagination={pagination} loading={rowsLoading}
              pkColumn={rowPkColumn} isMobile={isMobile}
              onPageChange={(p) => loadRows(selected, p, pagination.limit)}
              onEditRow={openRowEdit}
            />
          )}
        </>
      )}
    </div>
  );

  return (
    <div style={pageStyle}>
      {/* ── 페이지 헤더: 텍스트 제목 + 부제(menuConfig desc) — 직원 관리 표준 ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>DB 관리</h2>
          <div style={stdPageSubtitle}>DB 데이터를 조회·수정합니다. 민감 컬럼(비밀번호, 토큰 등)은 자동 마스킹 및 수정 불가입니다.</div>
        </div>
      </div>

      {isMobile
        ? (selected ? DetailPanelContent : TableListPanel)
        : <div style={layoutStyle}>{TableListPanel}{DetailPanelContent}</div>
      }

      {/* 행 편집 모달 */}
      {editRow && (
        <RowEditModal row={editRow} columns={rowCols} pkColumn={rowPkColumn}
          tableName={selected} onClose={() => setEditRow(null)} onSave={handleRowSave} />
      )}
    </div>
  );
}
